import { useContext, useEffect, useState } from "react";

import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { Box, FormControl, Grid, IconButton, InputAdornment, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { FinancialIcon } from "components/Icons/Icons";
import usePrompt from "components/Utility/usePrompt";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { ValueLabel } from "mui-toolkit/ValueLabel";
import { useSnackbar } from "notistack";
import { formatNumber, formatPrice } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import { useChatAssistantAttachments } from "../../../contexts/useChatAssistantAttachments";
import useFinanceReport from "../useFinanceReport";
import { INCOME_STATEMENT_REQUIRED_FILEDS } from "./IncomeStatementHelper";

export default function IncomeStatementWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const { enqueueSnackbar } = useSnackbar();

  const { component: confirmDialogComponent } = useDialog();
  const { component: formStatusComponent, actions: formStatusActions } = useFormStatus();

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);
  const { addAttachments } = useChatAssistantAttachments();

  const { state: lineItems, actions: lineItemActions } = useCardController();
  const prompt = usePrompt();

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const {
    isLoading,

    isLoadingBudget,
    budget,

    loadIncomeStatement,
    isLoadingIncomeStatement,
    incomeStatement,

    isLoadingRatios,
    ratios,
  } = useFinanceReport({
    onError: handleError,
    currentYear,
  });

  useEffect(() => {
    if (incomeStatement) {
      if (incomeStatement.income_statement_default_values) {
        updateIncomeStatementDefaultValues(incomeStatement.values);
      }
      lineItemActions.replace(incomeStatement?.values);
    }
    addAttachments([{ name: "Income Statement", content: incomeStatement }]);
  }, [incomeStatement]);

  // --- --- ---

  const updateIncomeStatementDefaultValues = async (newIncomeStatement) => {
    const updatedValues = newIncomeStatement.map((item) => {
      return {
        ...item,
        id: item?.id || uuidv4(),
      };
    });

    if (updatedValues.length > 0) {
      formStatusActions.setIsFormDirty(true, true);

      const _uploadFormData = {
        producer_token: globalState.producer_token,
        agent_token: globalState.agent_token,
        Document: {
          id: updatedValues[0]?.document_id || uuidv4(),
          values: updatedValues,
        },
      };

      await UsersApiHelper.updateFinancialDocument(incomeStatement, globalState);
      await updateDocumentDictionaryData(_uploadFormData);
    }
  };

  const handleChangeState_LineItem = async (event, _currentDocumentDictionary) => {
    if (event?.target?.value != lineItems.find((v) => v.id == _currentDocumentDictionary?.id)?.value) {
      formStatusActions.setIsFormDirty(true, true);

      const _newValue = {
        ..._currentDocumentDictionary,
        id: _currentDocumentDictionary?.id || uuidv4(),
        value: event?.target?.value || 0,
      };

      const _uploadFormData = {
        producer_token: globalState.producer_token,
        agent_token: globalState.agent_token,
        Document: {
          id: _newValue?.document_id || uuidv4(),
          values: [_newValue],
        },
      };

      if (_currentDocumentDictionary?.new_field) {
        await UsersApiHelper.updateFinancialDocument(incomeStatement, globalState);
      }

      await updateDocumentDictionaryData(_uploadFormData);
    }
  };

  const updateDocumentDictionaryData = async (_uploadFormData) => {
    UsersApiHelper.updateDocumentDictionary(_uploadFormData, globalState)
      .then(() => {
        formStatusActions.setIsSaving(false);
        loadIncomeStatement();
      })
      .catch((err) => {
        formStatusActions.setIsFormDirty(true, false);
        handleError(err);
      });
  };

  const formatName = (value) => {
    if (value) {
      return value.replaceAll("_", " ");
    }
  };

  // --- --- ---

  const handleClickAddNewLineItem = async (category) => {
    prompt.actions.prompt({
      title: "New Item",
      prompt: "Please enter item name:",
      callback: (inputValue) => {
        if (inputValue) {
          const foundItem = lineItems.find(
            (item) => item.item === formatString(inputValue) && item.container === category
          );

          if (foundItem) {
            enqueueSnackbar("Item already exists.", { preventDuplicate: true, variant: "warning" });
            return;
          } else {
            var newLineItem = createLineItem(category, inputValue, 0.0, false /*doOverwrite*/);
            if (newLineItem) {
              handleChangeState_LineItem({ target: { value: 0 } }, newLineItem);
            }
          }
        }
      },
    });
  };

  const createLineItem = (category, name, value, doOverwrite) => {
    const _id = doOverwrite && lineItems.find((li) => li?.name == name)?.id;

    const formattedName = formatString(name);

    if (formattedName != undefined && formattedName != null && formattedName != "") {
      const _newLineItem = {
        section: null,
        module: "financial",
        object: "income_statement",
        container: category,
        item: formattedName,
        id: _id || uuidv4(),
        document_id: incomeStatement?.id,
        value: 0,
        is_active: true,
        new_field: true,
      };

      return _newLineItem;
    } else {
      return;
    }
  };

  const handleDeleteLineItem = async (currentLineItem) => {
    const _newLineItem = { ...currentLineItem };
    _newLineItem.is_active = false;
    lineItemActions.update(_newLineItem);

    formStatusActions.setIsFormDirty(true, true);

    handleChangeState_LineItem({}, _newLineItem);
  };

  const formatString = (name) => {
    if (/[^a-zA-Z0-9\s]/.test(name)) {
      handleError({ message: "Only letters, numbers, and spaces are allowed." });
      return;
    }

    return name.toLowerCase().replace(/\s+/g, "_").replace(/_+/g, "_");
  };

  const emptyRecords = () => {
    return (
      !budget?.budgetCommodities?.length && (
        <Typography variant="body1" className="incomeStatementEmpty">
          No records found
        </Typography>
      )
    );
  };

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title="Income Statement"
        category={{
          title: "Financial Analysis",
          icon: <FinancialIcon />,
        }}
      />

      <WorkspacePageHeader
        title="Year"
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        isLoading={isLoading}
        additionalControls={formStatusComponent}
      />

      <Grid container columnSpacing={6} rowSpacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Revenue from Operations
          </Typography>

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, textTransform: "capitalize" }}>
              Farm Revenue
            </Typography>
            <Grid container spacing={2}>
              {Boolean(budget?.budgetCommodities?.length) &&
                budget?.budgetCommodities
                  ?.sort((a, b) => a.commodity_name?.localeCompare(b.commodity_name))
                  ?.map((currentCommodity, index) => (
                    <Grid item key={index} xs={6}>
                      <FormControl fullWidth={true}>
                        <StatefulControlledTextField
                          label={formatName(currentCommodity?.commodity_name)}
                          name={currentCommodity?.commodity_name}
                          isRequired={false}
                          defaultValue={formatNumber(currentCommodity.revenue, 0)}
                          variant="outlined"
                          disabled={true}
                          onChange={(e) => handleChangeState_LineItem(e, v)}
                          InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                          maskType={MASK_NUMERIC}
                        />
                      </FormControl>

                      <br />
                    </Grid>
                  ))}
            </Grid>
            {emptyRecords()}
          </Box>

          {incomeStatement?.revenue_containers?.map((container) => (
            <Box key={container} sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, textTransform: "capitalize" }}>
                {formatName(container)}
              </Typography>

              <Grid container spacing={2}>
                {Boolean(lineItems?.length) &&
                  lineItems
                    ?.filter((v) => v.container == container)
                    .map((v, index) => (
                      <Grid item key={index + "-" + v.item} xs={6}>
                        <FormControl fullWidth={true}>
                          <StatefulControlledTextField
                            label={formatName(v.item)}
                            name={v.item}
                            isRequired={false}
                            defaultValue={v.value}
                            variant="outlined"
                            onChange={(e) => handleChangeState_LineItem(e, v)}
                            InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                            endAdornment={
                              !INCOME_STATEMENT_REQUIRED_FILEDS.includes(v.item) && (
                                <InputAdornment position="end" className="delete">
                                  <IconButton onClick={() => handleDeleteLineItem(v)} edge="end" color="secondary">
                                    <DeleteOutlined />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                            maskType={MASK_NUMERIC}
                          />
                        </FormControl>

                        <br />
                      </Grid>
                    ))}

                <Grid
                  item
                  xs={6}
                  sx={{ mb: "20px", pt: "44px !important" }}
                  className="navigation"
                  display={"flex"}
                  alignItems={"end"}
                >
                  <Button
                    fullWidth
                    sx={{ borderRadius: 1 }}
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleClickAddNewLineItem(container)}
                  >
                    <AddCircleOutline />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Typography variant="h6">Totals</Typography>
          <Box className="flexWrapContainer">
            <ValueLabel
              label="Total Revenue from Operations"
              value={formatPrice(ratios?.revenue_operations, 0)}
              isLoading={isLoadingRatios}
            />
            <ValueLabel label="Farm Revenue" value={formatPrice(budget?.revenue, 0)} isLoading={isLoadingBudget} />
            <ValueLabel
              label="Other Revenue"
              value={formatPrice(incomeStatement?.revenue?.total, 0)}
              isLoading={isLoadingIncomeStatement}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6} className="pageBreakBefore">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Operating Costs & Expenses
          </Typography>

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, textTransform: "capitalize" }}>
              Production Costs
            </Typography>
            <Grid container spacing={2}>
              {Boolean(budget?.budgetCommodities?.length) &&
                budget?.budgetCommodities
                  ?.sort((a, b) => a.commodity_name?.localeCompare(b.commodity_name))
                  ?.map((currentCommodity, index) => (
                    <Grid item key={index} xs={6}>
                      <FormControl fullWidth={true}>
                        <StatefulControlledTextField
                          label={formatName(currentCommodity?.commodity_name)}
                          name={currentCommodity?.commodity_name}
                          isRequired={false}
                          defaultValue={formatNumber(currentCommodity.production_cost, 0)}
                          variant="outlined"
                          disabled={true}
                          onChange={(e) => handleChangeState_LineItem(e, v)}
                          InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                          maskType={MASK_NUMERIC}
                        />
                      </FormControl>

                      <br />
                    </Grid>
                  ))}
            </Grid>
            {emptyRecords()}
          </Box>

          {incomeStatement?.expenses_containers?.map((container) => (
            <Box key={container} sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, textTransform: "capitalize" }}>
                {formatName(container)}
              </Typography>

              <Grid container spacing={2}>
                {Boolean(lineItems?.length) &&
                  lineItems
                    ?.filter((v) => v.container == container)
                    .map((v, index) => (
                      <Grid item key={index} xs={6}>
                        <FormControl fullWidth={true}>
                          <StatefulControlledTextField
                            label={formatName(v.item)}
                            name={v.item}
                            isRequired={false}
                            defaultValue={v.value}
                            variant="outlined"
                            onChange={(e) => handleChangeState_LineItem(e, v)}
                            InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                            endAdornment={
                              !INCOME_STATEMENT_REQUIRED_FILEDS.includes(v.item) && (
                                <InputAdornment position="end" className="delete">
                                  <IconButton onClick={() => handleDeleteLineItem(v)} edge="end" color="secondary">
                                    <DeleteOutlined />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                            maskType={MASK_NUMERIC}
                          />
                        </FormControl>

                        <br />
                      </Grid>
                    ))}
                <Grid
                  item
                  xs={6}
                  sx={{ mb: "20px", pt: "44px !important" }}
                  className="navigation"
                  display={"flex"}
                  alignItems={"end"}
                >
                  <Button
                    fullWidth
                    sx={{ borderRadius: 1 }}
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleClickAddNewLineItem(container)}
                  >
                    <AddCircleOutline />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Typography variant="h6">Totals</Typography>
          <Box className="flexWrapContainer">
            <ValueLabel
              label="Total Costs and Expenses"
              value={formatPrice(budget?.expenses?.total + incomeStatement?.expenses?.total, 0)}
              sx={{ fontWeight: "bold !important" }}
              isLoading={isLoadingRatios}
            />
            <ValueLabel
              label="Production Cost"
              value={formatPrice(budget?.production_cost, 0)}
              isLoading={isLoadingBudget}
            />
            <ValueLabel
              label="Other Expenses"
              value={formatPrice(incomeStatement?.expenses?.total, 0)}
              isLoading={isLoadingIncomeStatement}
            />
            <ValueLabel
              label="Total Interest Expense"
              value={formatPrice(incomeStatement?.expenses_interest?.total, 0)}
              isLoading={isLoadingIncomeStatement}
            />
            <ValueLabel
              label="Depreciation Expense"
              value={formatPrice(incomeStatement?.expenses_depreciation?.total, 0)}
              isLoading={isLoadingIncomeStatement}
            />
            <ValueLabel
              label="Net Income"
              value={formatPrice(ratios?.net_income, 0)}
              sx={{ fontWeight: "bold !important" }}
              isLoading={isLoadingRatios}
            />
          </Box>
        </Grid>
      </Grid>

      <WorkspaceActionBar content={<></>} />

      {prompt?.component}
      {confirmDialogComponent}
    </>
  );
}
